import { supabase } from '../lib/supabase';

export interface GiftCardSettings {
  id?: string;
  business_id: string;
  
  // Card Styling
  primary_color: string;
  secondary_color: string;
  background_color: string;
  text_color: string;
  accent_color: string;
  
  // Logo Settings (uses business logo from booking_settings)
  include_logo: boolean;
  logo_size: 'small' | 'medium';
  logo_position: 'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  logo_max_height: number;
  
  // Card Content
  card_title: string;
  card_title_position: 'left' | 'center' | 'right';
  greeting_message: string;
  greeting_message_position: 'left' | 'center' | 'right';
  
  // Card Design Options
  card_style: 'classic' | 'modern' | 'elegant' | 'playful';
  show_decorative_elements: boolean;
  border_radius: 'sharp' | 'rounded' | 'pill';
  
  // Typography
  font_family: 'inter' | 'roboto' | 'playfair' | 'poppins' | 'montserrat';
  
  // Timestamps
  created_at?: string;
  updated_at?: string;
}

export const defaultGiftCardSettings: Partial<GiftCardSettings> = {
  primary_color: '#3B82F6',
  secondary_color: '#1E40AF',
  background_color: '#FFFFFF',
  text_color: '#1F2937',
  accent_color: '#F59E0B',
  include_logo: true,
  logo_size: 'medium',
  logo_position: 'top-center',
  logo_max_height: 48,
  card_title: 'Gift Card',
  card_title_position: 'center',
  greeting_message: 'Give the gift of choice',
  greeting_message_position: 'center',
  card_style: 'modern',
  show_decorative_elements: true,
  border_radius: 'rounded',
  font_family: 'inter',
};

export class GiftCardSettingsService {
  /**
   * Get gift card settings for a business
   */
  static async getGiftCardSettings(businessId: string): Promise<GiftCardSettings | null> {
    try {
      if (!supabase) {
        console.error('Supabase client not initialized');
        return null;
      }

      const { data, error } = await supabase
        .from('gift_card_settings')
        .select('*')
        .eq('business_id', businessId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No settings found, return null to indicate default settings should be used
          console.log('No gift card settings found for business:', businessId);
          return null;
        }
        throw error;
      }

      return data as GiftCardSettings;
    } catch (error) {
      console.error('Error fetching gift card settings:', error);
      return null;
    }
  }

  /**
   * Create or update gift card design
   */
  static async saveGiftCardSettings(businessId: string, settings: Partial<GiftCardSettings>): Promise<boolean> {
    try {
      if (!supabase) {
        console.error('Supabase client not initialized');
        return false;
      }

      if (settings.id) {
        // Update existing design (editing)
        console.log('📝 Updating existing design:', settings.id);
        const { error } = await supabase
          .from('gift_card_settings')
          .update({
            ...settings,
            updated_at: new Date().toISOString()
          })
          .eq('id', settings.id);

        if (error) throw error;
      } else {
        // Create new design
        console.log('✨ Creating new design');
        const { error } = await supabase
          .from('gift_card_settings')
          .insert({
            business_id: businessId,
            ...defaultGiftCardSettings,
            ...settings,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
      }

      return true;
    } catch (error) {
      console.error('Error saving gift card settings:', error);
      return false;
    }
  }
}

